(() => {
  const moneyFormatter = (currency) => (cents) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  };

  function initProduct(root) {
    if (!root || root.dataset.lfbInitialized === 'true') return;
    root.dataset.lfbInitialized = 'true';

    const productNode = root.querySelector('[data-lfb-product-json]');
    const orderNode = root.querySelector('[data-lfb-order-json]');
    const product = productNode ? JSON.parse(productNode.textContent) : null;
    const orderData = orderNode ? JSON.parse(orderNode.textContent) : {};
    if (!product?.variants?.length) return;

    const currency = root.dataset.currency || 'USD';
    const formatMoney = moneyFormatter(currency);
    const whatsappBase = root.dataset.whatsappUrl || '';
    const primaryActionBase = root.dataset.primaryActionUrl || '';
    const primaryActionLabel = root.dataset.primaryActionLabel || 'Payment: PayPal, Bank transfer';
    const primaryActionMobileLabel = root.dataset.primaryActionMobileLabel || primaryActionLabel;
    const scrollZoom = Number(root.dataset.scrollZoom || 1.08);
    const qs = (selector) => root.querySelector(selector);
    const qsa = (selector) => Array.from(root.querySelectorAll(selector));
    const disallowedCombinations = (root.dataset.disallowedCombinations || '')
      .split(/\r?\n/)
      .map((line) => line.split('|').map((value) => value.trim()).filter(Boolean))
      .filter((values) => values.length);

    function isDisallowedVariant(variant) {
      return disallowedCombinations.some((combination) => (
        combination.length === variant.options.length
        && combination.every((value, index) => value === variant.options[index])
      ));
    }

    const initialId = Number(root.dataset.selectedVariantId || 0);
    let currentVariant = product.variants.find((variant) => Number(variant.id) === initialId)
      || product.variants.find((variant) => variant.available && !isDisallowedVariant(variant))
      || product.variants.find((variant) => !isDisallowedVariant(variant))
      || product.variants[0];
    if (isDisallowedVariant(currentVariant)) {
      currentVariant = product.variants.find((variant) => variant.available && !isDisallowedVariant(variant))
        || product.variants.find((variant) => !isDisallowedVariant(variant))
        || currentVariant;
    }
    let selectedOptions = [...currentVariant.options];
    let currentImageIndex = 0;

    const variantIdInput = qs('[data-lfb-variant-id]');
    const priceEl = qs('[data-lfb-price]');
    const compareEl = qs('[data-lfb-compare]');
    const savingEl = qs('[data-lfb-saving]');
    const totalEl = qs('[data-lfb-total]');
    const mobileTotalEl = qs('[data-lfb-mobile-total]');
    const orderBox = qs('[data-lfb-order-status]');
    const orderTitle = qs('[data-lfb-order-title]');
    const orderLead = qs('[data-lfb-order-lead]');
    const submitButton = qs('[data-lfb-submit]');
    const mobileSubmit = qs('[data-lfb-mobile-submit]');
    const primaryContact = qs('[data-lfb-primary-contact]');
    const mobilePrimaryContact = qs('[data-lfb-mobile-primary-contact]');
    const messageEl = qs('[data-lfb-message]');
    const form = qs('[data-lfb-product-form]');
    const mainImage = qs('[data-lfb-main-image]');
    const lightbox = qs('[data-lfb-lightbox]');
    const lightboxImage = qs('[data-lfb-lightbox-image]');
    const thumbs = qsa('[data-lfb-image]');

    function selectedUpgradeTotal() {
      return qsa('[data-lfb-upgrade-id].is-selected').reduce(
        (total, item) => total + Number(item.dataset.lfbUpgradePrice || 0),
        0
      );
    }

    function findVariant(options) {
      return product.variants.find((variant) => (
        !isDisallowedVariant(variant)
        && variant.options.every((value, index) => value === options[index])
      ));
    }

    function variantExistsForOption(index, value) {
      return product.variants.some((variant) => {
        if (isDisallowedVariant(variant)) return false;
        if (variant.options[index] !== value) return false;
        return variant.options.every((optionValue, optionIndex) => optionIndex === index || optionValue === selectedOptions[optionIndex]);
      });
    }

    function getOrderInfoForVariant(variant) {
      return orderData[String(variant.id)] || {
        mode: 'pre_order',
        leadTime: root.dataset.defaultLeadTime || '',
        canAdd: Boolean(variant.available)
      };
    }

    function getOrderInfo() {
      return getOrderInfoForVariant(currentVariant);
    }

    function selectedConfigurationProperties() {
      return qsa('[data-lfb-property-name]').reduce((properties, group) => {
        const selected = group.querySelector('[data-lfb-property-value].is-selected');
        const name = group.dataset.lfbPropertyName;
        if (name && selected?.dataset.lfbPropertyValue) {
          properties[name] = selected.dataset.lfbPropertyValue;
        }
        return properties;
      }, {});
    }

    function selectedUpgradeLabels() {
      return qsa('[data-lfb-upgrade-id].is-selected')
        .map((upgrade) => upgrade.querySelector('strong')?.textContent?.trim())
        .filter(Boolean);
    }

    function appendMessage(base, message) {
      if (!base) return '';
      if (base.toLowerCase().startsWith('mailto:')) return base;
      const separator = base.includes('?') ? '&' : '?';
      return `${base}${separator}text=${encodeURIComponent(message)}`;
    }

    function buildWhatsappUrl() {
      if (!whatsappBase) return '';
      const customConfiguration = Object.entries(selectedConfigurationProperties())
        .map(([name, value]) => `${name}: ${value}`)
        .join(', ');
      const configurationSuffix = customConfiguration ? `, ${customConfiguration}` : '';
      const message = `Hi 4LEAF Bikes, I would like to confirm a pre-order for ${product.title} - ${currentVariant.title}${configurationSuffix}.`;
      return appendMessage(whatsappBase, message);
    }

    function buildPrimaryActionUrl() {
      if (!primaryActionBase) return '';
      const properties = Object.entries(selectedConfigurationProperties())
        .map(([name, value]) => `${name}: ${value}`);
      const upgrades = selectedUpgradeLabels();
      const configuration = [
        ...properties,
        ...(upgrades.length ? [`Upgrades: ${upgrades.join(', ')}`] : [])
      ];
      const suffix = configuration.length ? `, ${configuration.join(', ')}` : '';
      const total = Number(currentVariant.price || 0) + selectedUpgradeTotal();
      const message = `Hi 4LEAF Bikes, I would like to arrange payment by PayPal or bank transfer for ${product.title} - ${currentVariant.title}${suffix}. Estimated total: ${formatMoney(total)}.`;
      return appendMessage(primaryActionBase, message);
    }

    function updateOptionButtons() {
      qsa('[data-lfb-option-index]').forEach((group) => {
        const index = Number(group.dataset.lfbOptionIndex);
        const showDeliveryStatus = (group.dataset.lfbOptionName || '').toLowerCase().includes('size');
        group.querySelectorAll('[data-lfb-option-value]').forEach((button) => {
          const value = button.dataset.lfbOptionValue;
          const displayValue = button.dataset.lfbOptionDisplay || value;
          const selected = selectedOptions[index] === value;
          const exists = variantExistsForOption(index, value);
          const candidateOptions = [...selectedOptions];
          candidateOptions[index] = value;
          const candidateVariant = findVariant(candidateOptions);
          const candidateOrder = candidateVariant ? getOrderInfoForVariant(candidateVariant) : null;
          const statusEl = button.querySelector('[data-lfb-option-status]');
          button.classList.toggle('is-selected', selected);
          button.classList.toggle('is-unavailable', !exists);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          if (showDeliveryStatus && candidateOrder && statusEl) {
            const statusText = candidateOrder.mode === 'in_stock' ? 'In stock' : 'Pre-order';
            button.dataset.orderMode = candidateOrder.mode;
            statusEl.textContent = statusText;
            statusEl.hidden = false;
            button.setAttribute('aria-label', exists ? `${displayValue}, ${statusText}` : `${displayValue}, configuration unavailable`);
          } else {
            delete button.dataset.orderMode;
            if (statusEl) statusEl.hidden = true;
            button.setAttribute('aria-label', exists ? displayValue : `${displayValue}, configuration unavailable`);
          }
        });
      });
    }

    function updatePanel() {
      currentVariant = findVariant(selectedOptions) || currentVariant;
      const variantPrice = Number(currentVariant.price || 0);
      const comparePrice = Number(currentVariant.compare_at_price || 0);
      const saving = comparePrice > variantPrice ? comparePrice - variantPrice : 0;
      const total = variantPrice + selectedUpgradeTotal();
      const order = getOrderInfo();
      const isPreOrder = order.mode === 'pre_order';
      const canAdd = Boolean(order.canAdd ?? currentVariant.available);

      if (variantIdInput) variantIdInput.value = currentVariant.id;
      if (priceEl) priceEl.textContent = formatMoney(variantPrice);
      if (compareEl) {
        compareEl.textContent = comparePrice ? formatMoney(comparePrice) : '';
        compareEl.classList.toggle('is-hidden', !saving);
      }
      if (savingEl) {
        savingEl.textContent = saving ? `Save ${formatMoney(saving)}` : '';
        savingEl.classList.toggle('is-hidden', !saving);
      }
      if (totalEl) totalEl.textContent = formatMoney(total);
      if (mobileTotalEl) mobileTotalEl.textContent = formatMoney(total);

      if (orderBox) orderBox.dataset.orderMode = order.mode;
      if (orderTitle) orderTitle.textContent = isPreOrder ? 'Pre-order' : 'In stock';
      if (orderLead) {
        orderLead.textContent = isPreOrder
          ? (order.leadTime || root.dataset.defaultLeadTime || 'Estimated dispatch will be confirmed after ordering.')
          : (order.leadTime || 'Ready to prepare for dispatch.');
      }

      const buttonText = isPreOrder
        ? (canAdd ? 'Pre-order this build' : 'Ask about this pre-order')
        : (canAdd ? 'Add to cart' : 'Currently unavailable');
      if (submitButton) {
        submitButton.disabled = !isPreOrder && !canAdd;
        submitButton.textContent = buttonText;
      }
      if (mobileSubmit) {
        mobileSubmit.disabled = !isPreOrder && !canAdd;
        mobileSubmit.textContent = isPreOrder ? 'Pre-order' : (canAdd ? 'Add to cart' : 'Unavailable');
      }
      const primaryActionUrl = buildPrimaryActionUrl();
      if (primaryContact) {
        primaryContact.href = primaryActionUrl || primaryActionBase;
        primaryContact.textContent = primaryActionLabel;
      }
      if (mobilePrimaryContact) {
        mobilePrimaryContact.href = primaryActionUrl || primaryActionBase;
        mobilePrimaryContact.textContent = primaryActionMobileLabel;
      }
      if (messageEl) messageEl.textContent = '';

      updateOptionButtons();
      root.dispatchEvent(new CustomEvent('lfb:variant-change', {
        bubbles: true,
        detail: { variant: currentVariant, order }
      }));
    }

    qsa('[data-lfb-option-index]').forEach((group) => {
      const index = Number(group.dataset.lfbOptionIndex);
      group.querySelectorAll('[data-lfb-option-value]').forEach((button) => {
        button.addEventListener('click', () => {
          selectedOptions[index] = button.dataset.lfbOptionValue;
          const exact = findVariant(selectedOptions);
          if (!exact) {
            const fallback = product.variants.find((variant) => (
              !isDisallowedVariant(variant)
              && variant.options[index] === selectedOptions[index]
            ));
            if (fallback) selectedOptions = [...fallback.options];
          }
          updatePanel();
        });
      });
    });

    qsa('[data-lfb-property-name]').forEach((group) => {
      group.querySelectorAll('[data-lfb-property-value]').forEach((button) => {
        button.addEventListener('click', () => {
          group.querySelectorAll('[data-lfb-property-value]').forEach((item) => {
            const selected = item === button;
            item.classList.toggle('is-selected', selected);
            item.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
        });
      });
    });

    qsa('[data-lfb-upgrade-id]').forEach((button) => {
      button.addEventListener('click', () => {
        const selected = button.classList.toggle('is-selected');
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        updatePanel();
      });
    });

    function showImage(index) {
      if (!thumbs.length || !mainImage) return;
      currentImageIndex = (index + thumbs.length) % thumbs.length;
      const thumb = thumbs[currentImageIndex];
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = thumb.dataset.lfbImage;
      mainImage.alt = thumb.dataset.lfbImageAlt || product.title;
      thumbs.forEach((item, itemIndex) => {
        const active = itemIndex === currentImageIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (lightboxImage) {
        lightboxImage.src = thumb.dataset.lfbImageFull || thumb.dataset.lfbImage;
        lightboxImage.alt = mainImage.alt;
      }
    }

    thumbs.forEach((button, index) => button.addEventListener('click', () => showImage(index)));

    qs('[data-lfb-open-lightbox]')?.addEventListener('click', () => {
      showImage(currentImageIndex);
      if (typeof lightbox?.showModal === 'function') lightbox.showModal();
    });
    qs('[data-lfb-lightbox-close]')?.addEventListener('click', () => lightbox?.close());
    qs('[data-lfb-lightbox-previous]')?.addEventListener('click', () => showImage(currentImageIndex - 1));
    qs('[data-lfb-lightbox-next]')?.addEventListener('click', () => showImage(currentImageIndex + 1));
    lightbox?.addEventListener('click', (event) => {
      if (event.target === lightbox) lightbox.close();
    });

    let zoomTicking = false;
    function updateScrollZoom() {
      zoomTicking = false;
      if (!mainImage || window.innerWidth <= 990 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        mainImage?.style.setProperty('--lfb-scroll-scale', '1');
        return;
      }
      const rect = root.getBoundingClientRect();
      const scrollable = Math.max(root.offsetHeight - window.innerHeight, 1);
      const progress = Math.min(1, Math.max(0, -rect.top / scrollable));
      const scale = 1 + progress * Math.max(0, scrollZoom - 1);
      mainImage.style.setProperty('--lfb-scroll-scale', scale.toFixed(3));
    }

    window.addEventListener('scroll', () => {
      if (zoomTicking) return;
      zoomTicking = true;
      window.requestAnimationFrame(updateScrollZoom);
    }, { passive: true });
    window.addEventListener('resize', updateScrollZoom, { passive: true });

    async function addToCart(event) {
      event?.preventDefault();
      const order = getOrderInfo();
      const canAdd = Boolean(order.canAdd ?? currentVariant.available);
      const isPreOrder = order.mode === 'pre_order';

      if (!canAdd) {
        const whatsappUrl = buildWhatsappUrl();
        if (isPreOrder && whatsappUrl) window.open(whatsappUrl, '_blank', 'noopener');
        return;
      }

      const properties = selectedConfigurationProperties();
      if (isPreOrder) {
        properties['Order type'] = 'Pre-order';
        properties['Estimated dispatch'] = order.leadTime || root.dataset.defaultLeadTime || 'To be confirmed';
      }
      const items = [{ id: Number(currentVariant.id), quantity: 1, properties }];
      qsa('[data-lfb-upgrade-id].is-selected').forEach((upgrade) => {
        items.push({ id: Number(upgrade.dataset.lfbUpgradeId), quantity: 1 });
      });

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = isPreOrder ? 'Adding pre-order...' : 'Adding...';
      }
      if (messageEl) messageEl.textContent = '';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('Cart add failed');
        window.location.href = '/cart';
      } catch (error) {
        updatePanel();
        if (messageEl) {
          messageEl.textContent = isPreOrder
            ? 'This pre-order needs confirmation. Please ask our bike expert on WhatsApp.'
            : 'This configuration could not be added. Please try again or contact us.';
        }
      }
    }

    if (primaryContact) {
      form?.addEventListener('submit', (event) => {
        event.preventDefault();
        window.open(buildPrimaryActionUrl() || primaryActionBase, '_blank', 'noopener');
      });
    } else {
      form?.addEventListener('submit', addToCart);
    }
    mobileSubmit?.addEventListener('click', () => form?.requestSubmit());
    showImage(0);
    updatePanel();
    updateScrollZoom();
  }

  function initSpecs(root) {
    if (!root || root.dataset.lfbInitialized === 'true') return;
    root.dataset.lfbInitialized = 'true';
    const buildCards = Array.from(root.querySelectorAll('[data-lfb-build-value]'));
    const jumpButtons = Array.from(root.querySelectorAll('[data-lfb-build-jump]'));
    const geometryDialog = root.querySelector('[data-lfb-geometry-dialog]');
    const geometryDialogImage = root.querySelector('[data-lfb-geometry-dialog-image]');

    function setSelectedBuild(value) {
      if (!value) return;
      buildCards.forEach((card) => card.classList.toggle('is-selected', card.dataset.lfbBuildValue === value));
      jumpButtons.forEach((button) => {
        const selected = button.dataset.lfbBuildJump === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    function selectPurchaseOption(value) {
      const purchaseRoot = document.querySelector('[data-lfb-product-v2]');
      if (!purchaseRoot) return;
      const optionButton = Array.from(purchaseRoot.querySelectorAll('[data-lfb-option-value]'))
        .find((button) => button.dataset.lfbOptionValue === value);
      optionButton?.click();
      purchaseRoot.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }

    jumpButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.lfbBuildJump;
        const card = buildCards.find((item) => item.dataset.lfbBuildValue === value);
        setSelectedBuild(value);
        card?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      });
    });

    buildCards.forEach((card) => {
      card.querySelector('[data-lfb-select-build]')?.addEventListener('click', () => selectPurchaseOption(card.dataset.lfbBuildValue));
    });

    document.addEventListener('lfb:variant-change', (event) => {
      const options = event.detail?.variant?.options || [];
      const match = buildCards.find((card) => options.includes(card.dataset.lfbBuildValue));
      if (match) setSelectedBuild(match.dataset.lfbBuildValue);
    });

    root.querySelectorAll('[data-lfb-geometry-open]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!geometryDialog || !geometryDialogImage) return;
        geometryDialogImage.src = button.dataset.lfbGeometryOpen;
        geometryDialogImage.alt = button.dataset.lfbGeometryAlt || 'Bike geometry chart';
        if (typeof geometryDialog.showModal === 'function') geometryDialog.showModal();
      });
    });
    root.querySelector('[data-lfb-geometry-close]')?.addEventListener('click', () => geometryDialog?.close());
    geometryDialog?.addEventListener('click', (event) => {
      if (event.target === geometryDialog) geometryDialog.close();
    });

    const purchaseRoot = document.querySelector('[data-lfb-product-v2]');
    if (purchaseRoot) {
      const selectedButtons = Array.from(purchaseRoot.querySelectorAll('[data-lfb-option-value].is-selected'));
      const matchingButton = selectedButtons.find((button) => buildCards.some((card) => card.dataset.lfbBuildValue === button.dataset.lfbOptionValue));
      if (matchingButton) setSelectedBuild(matchingButton.dataset.lfbOptionValue);
    }
  }

  function improveLegacyTables(scope = document) {
    scope.querySelectorAll('.lfb-product-description-v2 table tbody tr').forEach((row) => {
      const firstCell = row.firstElementChild;
      if (!firstCell || firstCell.tagName !== 'TD' || !firstCell.textContent.trim()) return;
      const rowHeader = document.createElement('th');
      Array.from(firstCell.attributes).forEach((attribute) => rowHeader.setAttribute(attribute.name, attribute.value));
      rowHeader.scope = 'row';
      rowHeader.innerHTML = firstCell.innerHTML;
      firstCell.replaceWith(rowHeader);
    });
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-lfb-product-v2]').forEach(initProduct);
    scope.querySelectorAll('[data-lfb-specs-v2]').forEach(initSpecs);
    improveLegacyTables(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
